/**
 * Copyright (C) 2018-present, Botobag Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *      http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Decimal } from 'decimal.js';

import assert from 'assert';

import { assertExists } from '@/lib/assert';
import {
  Money,
  MoneyAmountData,
} from '@/lib/money';

import {
  FulfillmentService,
  FulfillmentServiceData,
} from './fulfillmentService';
import {
  Image,
  ImageData,
  ImageTag,
} from './image';
import { Product } from './product';
import {
  ProductInventory,
  ProductInventoryData,
} from './productInventory';
import { ProductOption } from './productOption';

export interface ProductVariantData {
  readonly id: string;
  readonly name?: string;
  readonly options?: string[];
  readonly images?: ImageData[];
  readonly featureBullets?: string[];
  readonly inventory?: ProductInventoryData;
  readonly sku?: string;
  readonly price?: MoneyAmountData;
  readonly compareAtPrice?: MoneyAmountData;
  readonly fulfillments?: FulfillmentServiceData[];
}

export interface ProductVariantOption {
  readonly option: ProductOption;
  readonly value: string;
}

export class ProductVariant {
  public readonly options?: ProductVariantOption[];
  public readonly images?: Image[];
  public readonly inventory?: ProductInventory;
  public readonly price?: Money;
  public readonly compareAtPrice?: Money;
  public readonly fulfillments?: FulfillmentService[];

  constructor(
    public readonly product: Product,
    private readonly data: ProductVariantData,
  ) {
    if (data.options) {
      const options = assertExists(product.options);
      assert.strictEqual(data.options.length, options.length);
      this.options = data.options.map((value, index) => ({
        option: options[index],
        value,
      }));
    }

    if (data.images) {
      this.images = data.images.map((image) => (new Image(image)));
    } else {
      this.images = this.product.images;
    }

    if (data.inventory) {
      this.inventory = new ProductInventory(data.inventory);
    }

    if (data.price) {
      this.price = {
        amount: new Decimal(data.price),
        currency: product.priceCurrency,
      };
    }
    if (data.compareAtPrice) {
      this.compareAtPrice = {
        amount: new Decimal(data.compareAtPrice),
        currency: product.priceCurrency,
      };
    }

    if (data.fulfillments) {
      this.fulfillments =
        data.fulfillments.map((fulfillment) => (FulfillmentService.create(fulfillment)));
    }
  }

  get id() { return this.data.id; }
  get name() {
    if (this.data.name) {
      return this.data.name;
    } else {
      if (this.data.options) {
        return `${this.product.name} (${this.data.options.join('/')})`;
      } else {
        return this.product.name;
      }
    }
  }
  get image() {
    // Try to find primary image for the variant.
    const image = this.getImage(ImageTag.MainImage);
    return (image) ? image : this.product.image;
  }
  get featureBullets() {
    return (this.data.featureBullets) ? this.data.featureBullets : this.product.featureBullets;
  }
  get sku() { return this.data.sku; }

  get brand() { return this.product.brand; }
  get priceCurrency() { return this.product.priceCurrency; }

  public getImage(tag: string) {
    return (this.images) ? this.images.find((image) => (image.tag === tag)) : undefined;
  }

  // getOtherVariantsOnlyDifferFor returns all variants where values for all options are the same as
  // this `variant` except the one for the given option at `optionIdx`.
  public getOtherVariantsOnlyDifferFor(optionIdx: number): Map<string, ProductVariant> {
    const options = assertExists(this.options);
    const numOptions = options.length;
    assert.ok(numOptions > optionIdx);

    let result = new Map<string, ProductVariant>();
    this.product.variants.forEach((variant) => {
      const variantOptions = assertExists(variant.options);
      assert.strictEqual(variantOptions.length, options.length);

      for (let i = 0; i < numOptions; ++i) {
        const variantOption = variantOptions[i];
        assert.strictEqual(options[i].option, variantOption.option);

        // Skip the variants whose value are the same as `this.variant` for all options other
        // than `option`.
        if ((options[i].value !== variantOption.value) && (i !== optionIdx)) {
          return;
        }
      }

      // If we can pass the above loop, we find one qualified variant.
      const v = variantOptions[optionIdx].value;
      assert.ok(!result.has(v));
      result = result.set(v, variant);
    });

    return result;
  }

  // getOtherVariantsOnlyDifferFor returns all variants where values for all options are the same as
  // this `variant` except the ones for the given options indicated by `optionIndices`.
  public getOtherVariantsDifferFor(optionIdices: number[]): ProductVariant[] {
    const options = assertExists(this.options);
    const numOptions = options.length;
    assert.ok(numOptions >= optionIdices.length);
    assert.ok(!optionIdices.find((idx) => idx > numOptions));

    const result: ProductVariant[] = [];
    this.product.variants.forEach((variant) => {
      const variantOptions = assertExists(variant.options);
      assert.strictEqual(variantOptions.length, options.length);

      for (let i = 0; i < numOptions; ++i) {
        const variantOption = variantOptions[i];
        assert.strictEqual(options[i].option, variantOption.option);

        // Skip the variant if it has an option with value different from this variant and the
        // option is not listed in `optionIdices`.
        if ((options[i].value !== variantOption.value) &&
            !optionIdices.find((idx) => idx === i)) {
          return;
        }
      }

      // If we can pass the above loop, the variant is qualified. Add it to `result`.
      assert.ok(!result.find((v) => (v === variant)));
      result.push(variant);
    });

    return result;
  }
}
